/**
 * How Doug thinks about a place, not how the schema keys it.
 *
 * Pulled out of `Places.tsx` in WP 3.4c so the map's pins can colour by the
 * same seven groups the "By kind" list already sections by — a second
 * grouping written to match by eye is exactly this codebase's own bug
 * (CLAUDE.md: "one value doing two jobs"), so there is one function instead.
 */
export const GROUPS = [
  { id: 'stage', label: 'Concert halls and theatres' },
  { id: 'comedy', label: 'Comedy' },
  { id: 'cinema', label: 'Cinemas' },
  { id: 'museum', label: 'Museums' },
  { id: 'gallery', label: 'Galleries' },
  { id: 'market', label: 'Markets' },
  { id: 'other', label: 'Everywhere else' },
] as const

export type PlaceGroup = (typeof GROUPS)[number]['id']

/** Six kinds were added on 22 September and every one of them has to be
 *  routed here: an unrouted kind falls through to 'other', which would move
 *  65 venues into "Everywhere else" without anything looking broken. A
 *  church and a dance house are places you sit down in the evening, so they
 *  go with the stages; a Kunstverein and a private collection are curated
 *  and not selling, so they go with the museums; a project space is still a
 *  gallery. */
export function groupOf(kind: string | null): PlaceGroup {
  if (kind === 'building' || kind === 'theatre' || kind === 'hall' || kind === 'stage'
    || kind === 'church' || kind === 'dance') return 'stage'
  if (kind === 'kunstverein' || kind === 'collection') return 'museum'
  if (kind === 'project_space') return 'gallery'
  if (kind === 'cinema' || kind === 'museum' || kind === 'gallery' || kind === 'market'
    || kind === 'comedy') return kind
  return 'other'
}

/** A discipline hue per group, reusing the tokens `--d-*` already draws a
 *  mixed feed with (`tokens.css`) rather than growing the palette for seven
 *  more names nobody else needs. The pairing is by feel, not a shared
 *  meaning with the entry disciplines those tokens were named for — Doug can
 *  repaint any of these without touching a feed row.
 *
 *  Bare custom-property names, not `var(--x)` — a caller styling CSS wraps
 *  it (`Chip`'s `dot` prop wants the wrapped form); `PlaceMap` reads the
 *  resolved colour through `getComputedStyle` for Leaflet's canvas/SVG
 *  layer, which does not follow `var()` inside a paint attribute. */
export const GROUP_TOKEN: Record<PlaceGroup, string> = {
  stage: '--d-theatre',
  comedy: '--d-talk',
  cinema: '--d-film',
  museum: '--d-exhib',
  gallery: '--d-opera',
  market: '--d-market',
  other: '--ink-3',
}
